import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "./components/Layout";
import Dashboard from "./components/Dashboard";
import AdminFinal from "./components/AdminFinal";
import ConsultarPedidos from "./components/ConsultarPedidos";
import ConferenciaDiaria from "./components/ConferenciaDiaria";
import Analytics from "./components/Analytics";
import ConfiguracoesUnificadas from "./components/ConfiguracoesUnificadas";
import GerenciamentoAPIs from "./components/GerenciamentoAPIs";
import Feedbacks from "./components/Feedbacks";
import PesquisaSatisfacao from "./components/PesquisaSatisfacao";
import RastreioPublico from "./components/RastreioPublico";
import DebugPage from "./components/DebugPage";
import Login from "./components/Login";
import CotacaoFrete from "./components/CotacaoFrete";
import CotacaoOperacional from "./components/CotacaoOperacional";
import ImportarFabril from "./components/ImportarFabril";
import Importacoes from "./components/Importacoes";
import Validacao from "./components/Validacao";
import axios from 'axios';
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Redirecionar URL antiga para o domínio oficial
if (typeof window !== 'undefined' && window.location.hostname === 'primaza-tracker.emergent.host') {
  window.location.replace('https://www.rastreamentoprimaza.com.br' + window.location.pathname + window.location.search + window.location.hash);
}

// Função utilitária para formatar datas corretamente (evita problema de fuso horário)
export const formatDateBR = (dateString) => {
  if (!dateString) return '-';
  try {
    // Se já tem T (datetime ISO), usar diretamente
    if (dateString.includes('T')) {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Retorna original se inválido
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    // Se é só data, adiciona T12:00:00 para evitar problema de fuso horário
    const date = new Date(dateString + 'T12:00:00');
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('pt-BR');
  } catch (e) {
    return dateString || '-';
  }
};

// Função para formatar apenas data (sem hora)
export const formatDateOnlyBR = (dateString) => {
  if (!dateString) return '-';
  try {
    // Se já tem T (datetime ISO), pegar só a data
    if (dateString.includes('T')) {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('pt-BR');
    }
    // Se é só data
    const date = new Date(dateString + 'T12:00:00');
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('pt-BR');
  } catch (e) {
    return dateString || '-';
  }
};

// Função para criar Date object sem problema de timezone
export const parseDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString + 'T12:00:00');
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validacaoCount, setValidacaoCount] = useState(null);

  const loadValidacaoCount = async (token) => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/pedidos/validacao/count-cache`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setValidacaoCount(res.data?.incompletos ?? null);
    } catch (e) {
      // silencioso — badge é opcional
    }
  };

  useEffect(() => {
    const validateAndLoadUser = async () => {
      const token = localStorage.getItem('primaza_token');
      const userData = localStorage.getItem('primaza_user');
      
      if (token && userData) {
        try {
          // Configurar o token no axios
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Validar se o token ainda é válido fazendo uma requisição ao servidor
          const response = await axios.get(`${API}/auth/me`);
          
          if (response.data) {
            // Token válido - usar dados atualizados do servidor
            setUser(response.data);
            localStorage.setItem('primaza_user', JSON.stringify(response.data));
            // Carregar count UMA VEZ (não em cada navegação)
            if (['admin', 'lancador'].includes(response.data.role)) {
              loadValidacaoCount(token);
            }
          }
        } catch (error) {
          console.error('Token inválido ou expirado:', error);
          // Token inválido - limpar dados e forçar novo login
          localStorage.removeItem('primaza_token');
          localStorage.removeItem('primaza_user');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      
      setLoading(false);
    };
    
    validateAndLoadUser();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    const token = localStorage.getItem('primaza_token');
    if (token && ['admin', 'lancador'].includes(userData.role)) {
      loadValidacaoCount(token);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('primaza_token');
    localStorage.removeItem('primaza_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Rotas Públicas - SEMPRE acessíveis sem login */}
          <Route path="/debug" element={<DebugPage />} />
          <Route path="/pesquisa/:pedidoId" element={<PesquisaSatisfacao />} />
          <Route path="/rastreio/:codigo" element={<RastreioPublico tipo="universal" />} />
          <Route path="/t/:identificador" element={<RastreioPublico tipo="universal" />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          
          {/* Rotas Protegidas - Requerem login */}
          {user ? (
            <Route path="/*" element={
            <Layout user={user} onLogout={handleLogout} validacaoCount={validacaoCount}>
              <Routes>
                <Route path="/" element={<Dashboard user={user} />} />
                <Route path="/consultar" element={<ConsultarPedidos user={user} />} />
                <Route path="/cotacao-frete" element={<CotacaoFrete user={user} />} />
                <Route path="/cotacao-operacional" element={<CotacaoOperacional user={user} />} />
                
                {/* Rotas para Admin e Lançador */}
                {(user.role === 'admin' || user.role === 'lancador') && (
                  <>
                    <Route path="/conferencia-diaria" element={<ConferenciaDiaria user={user} />} />
                    <Route path="/validacao" element={<Validacao user={user} />} />
                    <Route path="/importacoes" element={<Importacoes user={user} />} />
                  </>
                )}
                
                {/* Rotas exclusivas do Admin */}
                {user.role === 'admin' && (
                  <>
                    <Route path="/admin" element={<AdminFinal user={user} defaultTab="config" />} />
                    <Route path="/admin/usuarios" element={<AdminFinal user={user} defaultTab="usuarios" />} />
                    <Route path="/admin/transportadoras" element={<ConfiguracoesUnificadas user={user} />} />
                    <Route path="/admin/analytics" element={<Analytics user={user} />} />
                    <Route path="/admin/feedbacks" element={<Feedbacks user={user} />} />
                    <Route path="/admin/apis" element={<GerenciamentoAPIs user={user} />} />
                    <Route path="/configuracoes" element={<ConfiguracoesUnificadas user={user} />} />
                    <Route path="/admin/importar-fabril" element={<ImportarFabril user={user} />} />
                  </>
                )}
              </Routes>
            </Layout>
          } />
          ) : (
            <>
              {/* Rastreio público na raiz: rastreamentoprimaza.com.br/{nfe} */}
              <Route path="/:identificador" element={<RastreioPublico tipo="universal" />} />
              <Route path="*" element={<Login onLogin={handleLogin} />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;